import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../../../services/web-api';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { removeSymbolsAndWhiteSpace } from '../../../../utils/text/text';
import { isDefined } from '../../../../utils/variables';
import { GcpXmlParser } from '../../../../viewer/gcp/gcp-lib/gcp-xml-parser';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { AppContext } from '../../../state/app-state';
import { useCompanyState } from '../../../state/company-state';
import { ProjectSpatialReferenceContext } from '../../../state/project-reference-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { getFileExtension } from '../../../utils/path';
import { ArtifactFolder, ArtifactItem } from './artifact-folder';

interface Props {
  resourceUrls: GeodataModel['resourceUrls'];
  selectedItem: Omit<GeodataModel, 'resourceUrls'>;
}

const artifactKeys = [
  // MetaShape artifacts.
  'report',
  'log',
  'project',
  'cameras',
  'boundary',
  'zip',
  'las',
  'laz',
  'tiled-model',
  'filtered_point_cloud',
  'filtered_point_cloud.las',
  'obj',
  'terrain_model',
  'dem',
  'tif',
  'tfw',
  'tif_01',
  'tfw_01',

  // ODM artifacts.
  'odm_proj.txt',
  'odm_log.json',
  'odm_report.pdf',
  'odm_orthophoto.tif',
  'odm_sampled_pointcloud.las',
  'odm_original_pointcloud.laz',
  'odm_filtered_point_cloud.las',

  'colmap_settings.json',
  'colmap_sparse_point_cloud.ply',
  'colmap_gpu_cpu_usage.csv',
  'colmap_log.txt',
  'colmap_point_cloud.las',

  'photomesh_report',
] as const;

type ArtifactKey = (typeof artifactKeys)[number];

interface Resource {
  icon: FontAwesomeIconProps['icon'];
  titleKey: string;
}

const resources: Record<ArtifactKey, Resource> = {
  report: { icon: 'file-pdf', titleKey: 'report' },
  project: { icon: 'file-archive', titleKey: 'project' },
  log: { icon: 'file-lines', titleKey: 'log' },
  cameras: { icon: 'file-csv', titleKey: 'cameras' },
  boundary: { icon: 'file-code', titleKey: 'boundary' },
  zip: { icon: 'file-archive', titleKey: 'zip' },
  las: { icon: 'file-export', titleKey: 'las' },
  laz: { icon: 'file-export', titleKey: 'laz' },
  'tiled-model': { icon: 'file-archive', titleKey: 'tiledModel' },
  filtered_point_cloud: { icon: 'file-export', titleKey: 'filteredPointCloudGeneric' },
  'filtered_point_cloud.las': {
    icon: 'file-export',
    titleKey: 'filteredPointCloudLas',
  },
  obj: { icon: 'file-code', titleKey: 'obj' },
  terrain_model: { icon: 'file-code', titleKey: 'terrainModel' },
  dem: { icon: 'file-image', titleKey: 'dem' },
  tif: { icon: 'file-image', titleKey: 'tif' },
  tfw: { icon: 'file-pen', titleKey: 'tfw' },
  tif_01: { icon: 'file-image', titleKey: 'tif01' },
  tfw_01: { icon: 'file-pen', titleKey: 'tfw01' },
  'odm_proj.txt': { icon: 'file-lines', titleKey: 'odmProj' },
  'odm_log.json': { icon: 'file-lines', titleKey: 'odmLog' },
  'odm_report.pdf': { icon: 'file-pdf', titleKey: 'odmReport' },
  'odm_orthophoto.tif': { icon: 'file-image', titleKey: 'odmOrtho' },
  'odm_sampled_pointcloud.las': { icon: 'file-export', titleKey: 'odmSampledPointCloud' },
  'odm_original_pointcloud.laz': { icon: 'file-export', titleKey: 'odmOriginalPointCloud' },
  'odm_filtered_point_cloud.las': { icon: 'file-export', titleKey: 'odmFilteredPointCloud' },
  'colmap_settings.json': { icon: 'file-code', titleKey: 'colmapSettings' },
  'colmap_sparse_point_cloud.ply': { icon: 'file-code', titleKey: 'colmapSparsePointCloud' },
  'colmap_gpu_cpu_usage.csv': { icon: 'file-csv', titleKey: 'colmapGpuCpuUsage' },
  'colmap_log.txt': { icon: 'file-lines', titleKey: 'colmapLog' },
  'colmap_point_cloud.las': { icon: 'file-export', titleKey: 'colmapPointCloud' },
  photomesh_report: { icon: 'file-pdf', titleKey: 'photomesh_report' },
};

const resourceGroups: Record<string, { titleKey: string; values: ArtifactKey[] }> = {
  project: {
    titleKey: 'project',
    values: [
      'report',
      'log',
      'project',
      'cameras',
      'zip',
      'boundary',
      'odm_proj.txt',
      'odm_log.json',
      'odm_report.pdf',
      'colmap_settings.json',
      'colmap_gpu_cpu_usage.csv',
      'colmap_log.txt',
      'photomesh_report',
    ],
  },
  pointClouds: {
    titleKey: 'pointClouds',
    values: [
      'las',
      'laz',
      'filtered_point_cloud',
      'odm_sampled_pointcloud.las',
      'odm_filtered_point_cloud.las',
      'odm_original_pointcloud.laz',
      'colmap_sparse_point_cloud.ply',
      'colmap_point_cloud.las',
    ],
  },
  models3d: { titleKey: 'models3d', values: ['tiled-model', 'obj', 'terrain_model'] },
  models2d: {
    titleKey: 'models2d',
    values: ['dem', 'tif', 'tfw', 'tif_01', 'tfw_01', 'odm_orthophoto.tif'],
  },
};

const GeodataArtifacts = (props: Props) => {
  const { t } = useTranslation();
  const isNewAligner = useMemo(() => !props.selectedItem.isLegacyProcess, [props.selectedItem]);
  const { isOrganizationAdmin } = React.useContext(AppContext);
  const { project } = React.useContext(ProjectContext);
  const { coordinateSystem } = React.useContext(ProjectSpatialReferenceContext);
  const { getCompanyCountry } = useCompanyState();
  const filteredUrls = useMemo(() => {
    const alpha2Code = getCompanyCountry()?.alpha2Code;

    const isGermanCompany = isDefined(alpha2Code) && alpha2Code === 'DE';

    if (!isOrganizationAdmin && (isGermanCompany || isNewAligner)) {
      return props.resourceUrls?.filter((x) => x.name !== 'report');
    }

    return props.resourceUrls;
  }, [props.resourceUrls, isNewAligner, isOrganizationAdmin, getCompanyCountry]);

  const getGcpFileUrl = useCallback(
    async (url: string): Promise<void> => {
      let gcpFileContent: string | undefined;

      if (isNewAligner && props.selectedItem.gcpCollection?.points) {
        gcpFileContent = props.selectedItem.gcpCollection.points
          .map((point) => `${point.name};${point.enu[1]};${point.enu[0]};${point.enu[2]}`)
          .join('\n');
      } else {
        const xml = await WebApi.fetchS3Data(url);
        const gcpXmlParser = new GcpXmlParser(xml);

        if (gcpXmlParser.gcpPoints.length === 0) {
          publishNotification('err0511');
          return;
        }
        gcpFileContent = gcpXmlParser.gcpPoints
          .map((point) => `${point.name};${point.northing};${point.easting};${point.height}`)
          .join('\n');
      }

      const a = document.createElement('a');
      const fileType = 'csv';
      const coordinateSystemName = removeSymbolsAndWhiteSpace(coordinateSystem?.name ?? '');
      a.download = `${project.name}_${props.selectedItem.name}_${
        coordinateSystemName ?? 'points'
      }.csv`;
      const blob = new Blob([gcpFileContent], { type: fileType });
      a.href = URL.createObjectURL(blob);
      a.dataset.downloadurl = [fileType, a.download, a.href].join(':');
      a.click();
    },
    [isNewAligner, props.selectedItem, project.name, coordinateSystem?.name],
  );

  if (!filteredUrls) {
    return null;
  }

  const doesGeodataHaveGroupResources = (values: string[]) => {
    return filteredUrls?.some((resourceUrl) => values.includes(resourceUrl.name));
  };

  const createDownloadImageRequest = async () => {
    const result = await SkyMapAxiosServiceFactory.instance
      .createGeodataServiceV0()
      .createImageDownloadRequest({
        path: {
          geodataId: props.selectedItem.id,
        },
      });
    publish(SubscriptionTopic.WebsocketMessageRecieved, {
      message: 'BatchDownloadUpdated',
      status: 'RUNNING',
      batchDownloadId: result.batchDownloadId,
      fileName: `${project.name}_${format(Date.now(), 'yyyyMMdd_HHmm')}.zip`,
      progress: 0,
      projectName: project.name,
    });
    publish(SubscriptionTopic.ToastrMessage, {
      type: 'info',
      message: t('batchDownload.processTriggered', { ns: 'components' }),
    });
  };

  const resolveGeodataArtifactItemTitle = (titleKey: string) => {
    switch (titleKey) {
      case 'log':
        return props.selectedItem.isLegacyProcess
          ? ''
          : t('geodataPage.artifacts.items.log', { ns: 'cloudProcessing' });
      case 'boundary':
        return t('geodataPage.artifacts.items.boundary', {
          ns: 'cloudProcessing',
          extension: getFileExtension(props.selectedItem.boundaryFile ?? '').toUpperCase(),
        });
      case 'filteredPointCloud':
        return t('geodataPage.artifacts.items.filteredPointCloudGeneric', {
          ns: 'cloudProcessing',
          format: props.selectedItem.filteredPointCloud?.format.toUpperCase() ?? '',
        });
      case 'filteredPointCloudLas':
        return t('geodataPage.artifacts.items.filteredPointCloudLas', {
          ns: 'cloudProcessing',
        });
      case 'tiledModel':
        return t('geodataPage.artifacts.items.tiledModel', {
          ns: 'cloudProcessing',
        });
      case 'filteredPointCloudGeneric':
        return t('geodataPage.artifacts.items.filteredPointCloudGeneric', {
          ns: 'cloudProcessing',
          format: props.selectedItem.filteredPointCloud?.format.toUpperCase() ?? '',
        });
      case 'terrainModel':
        return t('geodataPage.artifacts.items.terrainModel', {
          ns: 'cloudProcessing',
        });
      case 'zip':
        return t('geodataPage.artifacts.items.zip', {
          ns: 'cloudProcessing',
        });
      case 'las':
        return t('geodataPage.artifacts.items.las', {
          ns: 'cloudProcessing',
        });
      case 'laz':
        return t('geodataPage.artifacts.items.laz', {
          ns: 'cloudProcessing',
        });
      case 'tiled-model':
        return t('geodataPage.artifacts.items.tiledModel', {
          ns: 'cloudProcessing',
        });
      case 'filtered_point_cloud':
        return t('geodataPage.artifacts.items.filteredPointCloudGeneric', {
          ns: 'cloudProcessing',
          format: props.selectedItem.filteredPointCloud?.format.toUpperCase() ?? '',
        });
      case 'odm_filtered_point_cloud.las':
        return t('geodataPage.artifacts.items.odmFilteredPointCloud', {
          ns: 'cloudProcessing',
        });
      case 'filtered_point_cloud.las':
        return t('geodataPage.artifacts.items.filteredPointCloudLas', {
          ns: 'cloudProcessing',
        });
      case 'obj':
        return t('geodataPage.artifacts.items.obj', {
          ns: 'cloudProcessing',
        });
      case 'terrain_model':
        return t('geodataPage.artifacts.items.terrainModel', {
          ns: 'cloudProcessing',
        });
      case 'colmap_settings.json':
        return t('geodataPage.artifacts.items.colmapSettings', {
          ns: 'cloudProcessing',
        });
      case 'colmap_sparse_point_cloud.ply':
        return t('geodataPage.artifacts.items.colmapSparsePointCloud', {
          ns: 'cloudProcessing',
        });
      case 'colmap_gpu_cpu_usage.csv':
        return t('geodataPage.artifacts.items.colmapGpuCpuUsage', {
          ns: 'cloudProcessing',
        });
      case 'colmap_log.txt':
        return t('geodataPage.artifacts.items.colmapLog', {
          ns: 'cloudProcessing',
        });
      case 'colmap_point_cloud.las':
        return t('geodataPage.artifacts.items.colmapPointCloud', {
          ns: 'cloudProcessing',
        });
      case 'project':
        return t('geodataPage.artifacts.items.project', {
          ns: 'cloudProcessing',
        });
      case 'report':
        return t('geodataPage.artifacts.items.report', {
          ns: 'cloudProcessing',
        });
      case 'cameras':
        return t('geodataPage.artifacts.items.cameras', {
          ns: 'cloudProcessing',
        });
      case 'gcp':
        return t('geodataPage.artifacts.items.gcp', {
          ns: 'cloudProcessing',
        });
      case 'dem':
        return t('geodataPage.artifacts.items.dem', {
          ns: 'cloudProcessing',
        });
      case 'tif':
        return t('geodataPage.artifacts.items.tif', {
          ns: 'cloudProcessing',
        });
      case 'tfw':
        return t('geodataPage.artifacts.items.tfw', {
          ns: 'cloudProcessing',
        });
      case 'tif01':
        return t('geodataPage.artifacts.items.tif01', {
          ns: 'cloudProcessing',
        });
      case 'tfw01':
        return t('geodataPage.artifacts.items.tfw01', {
          ns: 'cloudProcessing',
        });
      case 'odm_proj.txt':
        return t('geodataPage.artifacts.items.odmProj', {
          ns: 'cloudProcessing',
        });
      case 'odm_log.json':
        return t('geodataPage.artifacts.items.odmLog', {
          ns: 'cloudProcessing',
        });
      case 'odm_report.pdf':
        return t('geodataPage.artifacts.items.odmReport', {
          ns: 'cloudProcessing',
        });
      case 'odm_orthophoto.tif':
        return t('geodataPage.artifacts.items.odmOrtho', {
          ns: 'cloudProcessing',
        });
      case 'odm_sampled_pointcloud.las':
        return t('geodataPage.artifacts.items.odmSampledPointCloud', {
          ns: 'cloudProcessing',
        });
      case 'odm_original_pointcloud.laz':
        return t('geodataPage.artifacts.items.odmOriginalPointCloud', {
          ns: 'cloudProcessing',
        });
      case 'photomesh_report':
        return (
          t('geodataPage.artifacts.items.report', {
            ns: 'cloudProcessing',
          }) + ' (PhotoMesh)'
        );
      default:
        return '';
    }
  };

  const generateArtifactItems = (values: ArtifactKey[]) => {
    return values.reduce((prev: ArtifactItem[], cur: ArtifactKey): ArtifactItem[] => {
      const resource = filteredUrls?.find((resourceUrl) => resourceUrl.name === cur);

      if (isDefined(resource)) {
        const artifactItem = resources[cur];
        const title = resolveGeodataArtifactItemTitle(artifactItem.titleKey);

        if (isDefined(title) && title.length > 0) {
          prev.push({
            icon: artifactItem.icon,
            title,
            url: resource.url,
            downloadProcess:
              cur === 'cameras'
                ? getGcpFileUrl
                : cur === 'zip'
                  ? createDownloadImageRequest
                  : undefined,
          });
        }
      }
      return prev;
    }, []);
  };

  if ((filteredUrls?.length ?? 0) === 0) {
    return null;
  }

  const resolveGroupTitle = (titleKey: string) => {
    switch (titleKey) {
      case 'project':
        return t('geodataPage.artifacts.groups.project', { ns: 'cloudProcessing' });
      case 'pointClouds':
        return t('geodataPage.artifacts.groups.pointClouds', { ns: 'cloudProcessing' });
      case 'models3d':
        return t('geodataPage.artifacts.groups.models3d', { ns: 'cloudProcessing' });
      case 'models2d':
        return t('geodataPage.artifacts.groups.models2d', { ns: 'cloudProcessing' });
      default:
        return '';
    }
  };

  return (
    <Component>
      <h5>{t('geodataPage.artifacts.title', { ns: 'cloudProcessing' })}</h5>

      <Artifacts>
        {Object.entries(resourceGroups).map(
          ([key, group]) =>
            doesGeodataHaveGroupResources(group.values) && (
              <ArtifactFolder
                items={generateArtifactItems(group.values)}
                key={key}
                title={resolveGroupTitle(group.titleKey)}
              />
            ),
        )}
      </Artifacts>
    </Component>
  );
};

const Component = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  height: 100%;

  button {
    margin: 0.5em 0 0.5em 0;
    width: 10em;
  }
`;

const Artifacts = styled.div`
  border: 1px solid ${(props) => props.theme.color.gray.light};
  margin-right: 1em;
  margin-top: 0.5em;
`;

export { GeodataArtifacts };
